'use client'

import { useEffect, useState } from 'react'
import { DashboardChartBase } from '../DashboardChartBase/DashboardChartBase'
import { useKpiStore } from '@repo/store/useKpiStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { DashboardPanel } from '@repo/dashboard/DashboardPanel'
import { DashboardChartSettingsForm } from '@repo/dashboard/DashboardChartSettingsForm'

export const DashboardChart = () => {
  const [chartData, setChartData] = useState([])
  const { defaultKpiId, feeEarnerId } = useAuthenticationStore()
  const [selectedKpi, setSelectedKpi] = useState(null)
  const [panelOptions, setPanelOptions] = useState([])
  const [target, setTarget] = useState()
  const [targetIsDefault, setTargetIsDefault] = useState(true)
  const [gotKpiOptions, setGotKpiOptions] = useState(false)
  const {
    kpiOptions,
    fetchKpiOptions,
    fetchKpiData,
    setSelectedKpiId,
    selectedKpiId,
    feeEarnerIds,
    userSpecifiedTargets
  } = useKpiStore()

  // fetch and set the kpi options if needed
  useEffect(() => {
    if (!gotKpiOptions) fetchKpiOptions()
    if (kpiOptions?.length) setPanelOptions(kpiOptions)
  }, [kpiOptions, fetchKpiOptions, defaultKpiId, gotKpiOptions])

  // default to the user's default kpi, unless they have selected one
  useEffect(() => {
    if (kpiOptions) {
      setGotKpiOptions(true)

      if (kpiOptions.length) {
        const id = selectedKpiId || defaultKpiId || kpiOptions[0].kpiId
        const selectedKpi = kpiOptions.find((kpi) => kpi.kpiId === id)
        setSelectedKpi(selectedKpi)
        setSelectedKpiId(id)
      }
    }
  }, [selectedKpiId, kpiOptions, defaultKpiId, setSelectedKpiId])

  useEffect(() => {
    // the value might be 0, so we need to check if it exists rather than just checking if it's truthy
    const existingTargetIds = Object.keys(userSpecifiedTargets)
    const hasUserSpecifiedValue =
      existingTargetIds.includes(selectedKpiId) || existingTargetIds.includes(String(selectedKpiId))

    const userSpecifiedTarget = userSpecifiedTargets[selectedKpiId]
    const defaultTarget = selectedKpi?.target

    if (hasUserSpecifiedValue && userSpecifiedTarget !== defaultTarget) {
      setTarget(userSpecifiedTargets[selectedKpiId])
      setTargetIsDefault(false)
    } else if (selectedKpi?.target || selectedKpi?.target === 0) {
      setTarget(Number(selectedKpi.target))
      setTargetIsDefault(true)
    } else {
      setTarget(0)
      setTargetIsDefault(true)
    }
  }, [selectedKpiId, userSpecifiedTargets, selectedKpi])

  // fetch chart data when the kpi changes
  useEffect(() => {
    if (!selectedKpiId) return

    const fetchChartData = async () => {
      const chartData = await fetchKpiData({
        kpiId: selectedKpiId,
        feeEarnerIds: feeEarnerIds || (feeEarnerId ? [feeEarnerId] : null)
      })
      setChartData(chartData)
    }

    fetchChartData()
  }, [selectedKpiId, fetchKpiData, feeEarnerIds, feeEarnerId])

  const renderTitle = () => {
    if (selectedKpi) return `KPI: ${selectedKpi.description}`
    return `KPI`
  }

  return (
    <DashboardPanel
      data-testid="DashboardChart"
      title={renderTitle()}
      options={panelOptions}
      selectedOption={selectedKpiId}
      onOptionSelected={({ value }) => setSelectedKpiId(value)}
      sx={{ height: '100%' }}
      inlineConfig={
        <DashboardChartSettingsForm selectedKpi={selectedKpi} target={target} targetIsDefault={targetIsDefault} />
      }
    >
      <DashboardChartBase data={chartData} target={target} units={selectedKpi?.units} />
    </DashboardPanel>
  )
}
